<script lang="ts">
  import {uid} from 'uid';
  import {cn} from 'Catalyst/cn';
  import {type BaseInputProps, baseInputTvProps} from '.';
  import {getContext, setContext} from 'svelte';
  import type {CatalystConfig} from 'Catalyst/components/ThemeContext';
  import type {Writable} from 'svelte/store';
  import {
    baseInputCompoundVariantsSize40dpAndButtonTrueButtonSize,
    baseInputCompoundVariantsSize56dpAndButtonTrueButtonSize,
  } from 'Catalyst/tokens/output/base-input/base-input';

  // eslint-disable-next-line no-unused-vars
  type $$Props = BaseInputProps;

  export let id: BaseInputProps['id'] = uid();
  export let label: BaseInputProps['label'] = '';
  export let placeholder: BaseInputProps['placeholder'] = '';
  export let value: BaseInputProps['value'] = '';
  export let disabled: BaseInputProps['disabled'] =
    baseInputTvProps.defaultVariants.disabled;
  export let invalid: BaseInputProps['invalid'] = false;
  export let inputTestId: BaseInputProps['inputTestId'] = `${id}-base-input`;
  export let labelTestId: BaseInputProps['labelTestId'] = 'base-input-label';
  export let action: BaseInputProps['action'] = () => {};
  export let ref: HTMLInputElement = null;
  export let floating: BaseInputProps['floating'] = false;

  const catalystConfig = getContext<Writable<CatalystConfig>>('catalystConfig');
  export let size: BaseInputProps['size'] =
    $catalystConfig?.defaultVariants?.size;
  export let style: BaseInputProps['style'] = $catalystConfig?.defaultVariants
    ?.style as BaseInputProps['style'];

  const inputButtonSizes = {
    '40dp': baseInputCompoundVariantsSize40dpAndButtonTrueButtonSize,
    '56dp': baseInputCompoundVariantsSize56dpAndButtonTrueButtonSize,
  };

  setContext('inputButtonSize', inputButtonSizes[size] || '32dp');

  $: baseInputTvSlots = baseInputTvProps({
    size,
    disabled,
    floating: floating || !!value,
    invalid: !!invalid,
    style,
    showPlaceholder: placeholder && !label,
  });
  $: klass = $$restProps.class;

  $: ref?.setCustomValidity(invalid ? 'Please enter a valid value.' : '');
</script>

<div class={cn(baseInputTvSlots.base(), baseInputTvSlots.container(), klass)}>
  <div class={baseInputTvSlots.inputWrapper()}>
    <slot name="prefix" />
    <div class="flex-1">
      <slot name="hidden-input" />
      <slot name="input">
        <input
          {...$$restProps}
          {id}
          class={cn(baseInputTvSlots.input())}
          {disabled}
          {placeholder}
          bind:value
          data-testid={inputTestId}
          bind:this={ref}
          on:blur
          on:click
          on:input
          on:focus
          use:action
        />
      </slot>
      {#if label}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
        <label
          for={id}
          class={baseInputTvSlots.label()}
          data-testid={labelTestId}
          on:click
        >
          {label}
        </label>
      {/if}
    </div>
    <slot name="suffix" />
  </div>
</div>
